/**
 * locale-format.ts — Locale-aware date / number / currency formatters.
 *
 * Single source of truth for any user-visible date, time, or number.
 * Reads the active locale from the i18n store so formatters react to
 * language switches without page reload.
 *
 * Usage in a component:
 *   const fmt = useLocaleFormat()
 *   fmt.date(value)            // 06/05/2026 (locale separators)
 *   fmt.dateLong(value)        // 6 de maig de 2026 / 6 de mayo de 2026
 *   fmt.dateTime(value)
 *   fmt.time(value)
 *   fmt.currency(1234.5)       // 1.234,50 €
 *   fmt.number(1234.5)
 *
 * Outside React (services, utilities), use the standalone functions which
 * read the language from the i18next instance.
 */

import { useMemo } from "react"
import i18n from "@/i18n"
import { useLocaleStore, type Locale } from "@/stores/localeStore"

const BCP47: Record<string, string> = {
	ca: "ca-ES",
	es: "es-ES",
	en: "en-GB",
}

export function bcp47From(locale?: string): string {
	if (!locale) return BCP47.ca
	return BCP47[locale.toLowerCase().slice(0, 2)] ?? BCP47.ca
}

/** Reactive hook — re-renders when locale changes. */
export function useBcp47Locale(): string {
	const locale = useLocaleStore((s) => s.locale)
	return bcp47From(locale)
}

/** Non-reactive — for utilities/services. */
export function getCurrentBcp47Locale(): string {
	return bcp47From(i18n.language)
}

// ── Standalone formatters ────────────────────────────────────────────────

export function formatDate(
	value: Date | string | number,
	opts?: Intl.DateTimeFormatOptions,
	locale?: string,
): string {
	return new Date(value).toLocaleDateString(locale ?? getCurrentBcp47Locale(), opts)
}

export function formatDateTime(
	value: Date | string | number,
	opts?: Intl.DateTimeFormatOptions,
	locale?: string,
): string {
	return new Date(value).toLocaleString(locale ?? getCurrentBcp47Locale(), opts)
}

export function formatTime(
	value: Date | string | number,
	opts?: Intl.DateTimeFormatOptions,
	locale?: string,
): string {
	return new Date(value).toLocaleTimeString(locale ?? getCurrentBcp47Locale(), opts)
}

export function formatNumber(
	value: number,
	opts?: Intl.NumberFormatOptions,
	locale?: string,
): string {
	return new Intl.NumberFormat(locale ?? getCurrentBcp47Locale(), opts).format(value)
}

export function formatCurrency(
	value: number,
	currency = "EUR",
	opts?: Intl.NumberFormatOptions,
	locale?: string,
): string {
	return new Intl.NumberFormat(locale ?? getCurrentBcp47Locale(), {
		style: "currency",
		currency,
		...opts,
	}).format(value)
}

// ── Reactive hook for components ─────────────────────────────────────────

interface LocaleFormatHelpers {
	bcp47: string
	locale: Locale
	date: (v: Date | string | number, opts?: Intl.DateTimeFormatOptions) => string
	dateLong: (v: Date | string | number) => string
	dateShort: (v: Date | string | number) => string
	dateTime: (v: Date | string | number, opts?: Intl.DateTimeFormatOptions) => string
	time: (v: Date | string | number, opts?: Intl.DateTimeFormatOptions) => string
	number: (v: number, opts?: Intl.NumberFormatOptions) => string
	currency: (v: number, currency?: string, opts?: Intl.NumberFormatOptions) => string
}

export function useLocaleFormat(): LocaleFormatHelpers {
	const locale = useLocaleStore((s) => s.locale)
	const bcp47 = bcp47From(locale)
	return useMemo<LocaleFormatHelpers>(
		() => ({
			bcp47,
			locale,
			date: (v, opts) => new Date(v).toLocaleDateString(bcp47, opts),
			dateLong: (v) =>
				new Date(v).toLocaleDateString(bcp47, {
					day: "numeric",
					month: "long",
					year: "numeric",
				}),
			dateShort: (v) =>
				new Date(v).toLocaleDateString(bcp47, {
					day: "2-digit",
					month: "2-digit",
					year: "numeric",
				}),
			dateTime: (v, opts) => new Date(v).toLocaleString(bcp47, opts),
			time: (v, opts) =>
				new Date(v).toLocaleTimeString(bcp47, {
					hour: "2-digit",
					minute: "2-digit",
					...opts,
				}),
			number: (v, opts) => new Intl.NumberFormat(bcp47, opts).format(v),
			currency: (v, currency = "EUR", opts) =>
				new Intl.NumberFormat(bcp47, {
					style: "currency",
					currency,
					...opts,
				}).format(v),
		}),
		[bcp47, locale],
	)
}

// ── i18n sentinel decoder ────────────────────────────────────────────────
//
// Some user-visible strings (background-job currentStep / title) are produced
// server-side where we can't call useTranslations. The server emits a sentinel
// of the form:
//   __i18n__:<key>
//   __i18n__:<key>:<json-encoded-params>
// and the client decodes it using i18next at render time. Plain strings pass
// through unchanged so legacy rows keep working.

export const I18N_SENTINEL_PREFIX = "__i18n__:"

/** Encode an i18n reference into a plain string (used server-side too). */
export function encodeI18n(key: string, params?: Record<string, unknown>): string {
	if (params && Object.keys(params).length > 0) {
		return `${I18N_SENTINEL_PREFIX}${key}:${JSON.stringify(params)}`
	}
	return `${I18N_SENTINEL_PREFIX}${key}`
}

type Translator = (key: string, params?: Record<string, unknown>) => string

/** Resolve a possibly-sentinel string against a translator function. */
export function resolveI18nString(
	value: string | null | undefined,
	t: Translator,
): string {
	if (!value) return ""
	if (!value.startsWith(I18N_SENTINEL_PREFIX)) return value

	const rest = value.slice(I18N_SENTINEL_PREFIX.length)
	const colonIdx = rest.indexOf(":")
	if (colonIdx === -1) {
		return t(rest) || value
	}

	const key = rest.slice(0, colonIdx)
	const paramsRaw = rest.slice(colonIdx + 1)
	try {
		const params = JSON.parse(paramsRaw) as Record<string, unknown>
		return t(key, params) || value
	} catch {
		return t(key) || value
	}
}
